/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import {
  Anchor,
  Text,
  rem,
  useMantineTheme,
  type DefaultMantineColor,
  type StyleProp,
} from '@mantine/core';
import { RiExternalLinkLine, type RemixiconComponentType } from '@remixicon/react';
import { createElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { TextVariants } from '@fai/ui/components/typography/variants';

export function ExternalLinkWrapper({
  children,
  url,
  textVariant = TextVariants.BodyMd,
  icon = RiExternalLinkLine,
  color,
  iconSize = 16,
  title,
}: {
  children?: ReactNode;
  url: string;
  textVariant?: TextVariants;
  color?: StyleProp<DefaultMantineColor>;
  icon?: RemixiconComponentType | null;
  iconSize?: number;
  title?: string;
}) {
  const theme = useMantineTheme();

  return (
    <Anchor
      c={color ?? theme.pf.color.fg.cloudblue.default.base}
      href={url}
      target="_blank"
      lh={0}
      title={title}
    >
      <Text span={true} variant={textVariant}>
        {children}
      </Text>

      {icon &&
        createElement(icon, {
          size: iconSize,
          style: { marginLeft: rem(4), marginBottom: rem(-2) },
        })}
    </Anchor>
  );
}

export function InternalLinkWrapper({
  children,
  url,
  textVariant = TextVariants.BodyMd,
  icon = RiExternalLinkLine,
  color,
  iconSize = 'md',
  onClick,
}: {
  children?: ReactNode;
  url: string;
  textVariant?: TextVariants;
  color?: StyleProp<DefaultMantineColor>;
  icon?: RemixiconComponentType | null;
  iconSize?: 'md' | 'lg';
  onClick?: VoidFunction;
}) {
  const theme = useMantineTheme();

  return (
    <Link
      style={{
        color: `${color ?? theme.pf.color.fg.cloudblue.default.base}`,
        textDecoration: 'none',
      }}
      to={url}
      onClick={onClick}
    >
      <Text span={true} variant={textVariant}>
        {children}
      </Text>

      {icon &&
        createElement(icon, {
          size: iconSize === 'md' ? 16 : 18,
          style: { marginLeft: rem(4), marginBottom: rem(-2) },
        })}
    </Link>
  );
}
